import React, { useEffect, useMemo, useState } from 'react'
import { EmailsTable } from 'components/Tables/EmailsTable/EmailsTable'
import { Tabs } from 'components/FbUI/Tabs'
import apiService from 'services/api'
import { EmailMessage } from 'models/emailIntegration'
import { EmailTab } from './EmailsModalHeader'

function TabTitle({ title, count }: { title: string; count: number }) {
  return (
    <span className="text-base">
      <span className="font-semibold">{title}</span>
      <span className="font-light"> ({count})</span>
    </span>
  )
}

interface EmailsListViewProps {
  contactCompanyId: number
  forDoorProxyId?: number
  selectedTab: EmailTab
  onTabChange: (tab: EmailTab) => void
  onEmailClick: (email: EmailMessage) => void
  onEmailsChange?: (emails: EmailMessage[]) => void
}

export function EmailsListView({
  contactCompanyId,
  forDoorProxyId,
  selectedTab,
  onTabChange,
  onEmailClick,
  onEmailsChange,
}: EmailsListViewProps) {
  const api = apiService()
  const [counts, setCounts] = useState<{ received: number; sent: number }>({
    received: 0,
    sent: 0,
  })

  const TABLE_KEY = forDoorProxyId
    ? `emails-modal-door-${forDoorProxyId}-${selectedTab}`
    : `emails-modal-company-${contactCompanyId}-${selectedTab}`

  // Fetch counts on mount
  useEffect(() => {
    const baseParams = forDoorProxyId
      ? { door_proxy_id: forDoorProxyId }
      : { chain_proxy_id: contactCompanyId }

    // Fetch received count
    api.emailIntegration
      .getEmailMessageList({ ...baseParams, outbound: false, page: 1 })
      .then((res) => {
        setCounts((prev) => ({ ...prev, received: res.count }))
      })

    // Fetch sent count
    api.emailIntegration
      .getEmailMessageList({ ...baseParams, outbound: true, page: 1 })
      .then((res) => {
        setCounts((prev) => ({ ...prev, sent: res.count }))
      })
  }, [contactCompanyId, forDoorProxyId])

  const tabs = useMemo(
    () => [
      {
        key: 'received' as const,
        title: <TabTitle title="Received" count={counts.received} />,
      },
      {
        key: 'sent' as const,
        title: <TabTitle title="Sent" count={counts.sent} />,
      },
    ],
    [counts]
  )

  const baseFilters = useMemo(
    () => ({
      outbound: selectedTab === 'sent',
    }),
    [selectedTab]
  )

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <Tabs
          tabs={tabs}
          selectedTab={selectedTab}
          setSelectedTab={onTabChange}
          selectedColor="#15803d"
          noGap
        />
      </div>
      <EmailsTable
        tableKey={TABLE_KEY}
        forChainProxyId={contactCompanyId}
        forDoorProxyId={forDoorProxyId}
        disableUrlPagination
        disableExportButton
        baseFilters={baseFilters}
        filterIdentifierModifier={`_${selectedTab}`}
        onEmailClick={onEmailClick}
        onEmailsChange={onEmailsChange}
      />
    </div>
  )
}
